<template>
  <div
    ref="submenu"
    :class="
      bemBlockMod({
        open: !(isBreakpointDesktop && isComplexSubmenuChild) && isOpen,
        complex: hasComplexSubCategories,
        block: isParentBlockCategory,
      })
    "
    :style="cssCustomProps"
  >
    <button
      v-if="!isBreakpointDesktop"
      type="button"
      :class="[bemElement('back'), 'btn', 'btn--back-menu']"
      @click.prevent="handleCloseSubmenu"
    >
      {{ $msg('header.back', 'header') }}
    </button>
    <div :class="bemElement('wrapper')">
      <ul
        ref="list"
        :class="bemElement('list', { complex: hasComplexSubCategories })"
        :title="categoryTitle"
      >
        <TheMenuItem
          v-for="(category, index) in categories"
          :key="index"
          :category="category"
          :is-parent-block-category="isParentBlockCategory"
          :is-secondary-level="true"
          :is-complex-child="hasComplexSubCategories"
          :is-last-child="index === categories.length - 1"
          :is-submenu-open="categoryOpen === category.id"
          @menu:init-complex-item="calculateCustomProps"
          @menu:category-open="toggleSelectedCategory"
          @menu:update-height="updateSubmenuHeight"
        />
      </ul>
    </div>
  </div>
</template>
<script>
import { mapState } from 'vuex';
import { types } from '../store';
import bemClassesMixin from '../mixins/bemClassesMixin';
import windowResizeMixin from '../mixins/windowResizeMixin';

const { FRONT_CONFIG } = window;

export default {
  name: 'TheMenuSubmenu',
  components: {
    TheMenuItem: () => import('./TheMenuItem.vue'),
  },
  mixins: [bemClassesMixin, windowResizeMixin],
  props: {
    categories: {
      type: Array,
      default: () => [],
    },
    categoryTitle: {
      type: String,
      default: null,
    },
    isOpen: {
      type: Boolean,
      default: false,
    },
    hasComplexSubCategories: {
      type: Boolean,
      default: false,
    },
    isComplexSubmenuChild: {
      type: Boolean,
      default: false,
    },
    isParentBlockCategory: {
      type: Boolean,
      default: false,
    },
    complexPosition: {
      type: [Number, Boolean],
      default: 0,
    },
  },
  data: () => ({
    submenuWidth: 0,
    lastChildPosition: 0,
    submenuComplexPosition: -1,
    categoryOpen: null,
    dataChangeBreakpoint: FRONT_CONFIG.responsive.bp_1025,
  }),
  computed: {
    ...mapState({
      isClosedMenu: (state) => state.menu.isClosedMenu,
    }),
    customPropsReady() {
      return (
        (this.complexPosition !== 0 && this.lastChildPosition !== 0) ||
        (this.complexPosition === 0 && this.lastChildPosition === 0)
      );
    },
    cssCustomProps() {
      return {
        ...(this.isParentBlockCategory &&
          this.isBreakpointDesktop && {
            '--submenu-width': `${this.submenuBlockWidth}px`,
          }),
        ...(this.hasComplexSubCategories &&
          this.customPropsReady && {
            '--submenu-width': `${this.submenuWidth ?? 0}px`,
          }),
        ...(this.isComplexSubmenuChild &&
          this.isOpen &&
          !this.isBreakpointDesktop && {
            '--submenu-height': `${this.submenuHeight ?? 0}px`,
          }),
        ...(this.submenuComplexPosition >= 0 && {
          '--item-position': `${this.submenuComplexPosition}px`,
        }),
      };
    },
    submenuHeight() {
      return this.isOpen ? this.$refs.list.offsetHeight : 0;
    },
    submenuBlockWidth() {
      if (!this.isOpen || !this.$refs.submenu) return 0;
      const { x: menuItemX, width: menuItemWidth } =
        this.$refs.submenu.parentElement.getBoundingClientRect();
      return menuItemX + menuItemWidth + 16;
    },
  },
  watch: {
    isClosedMenu() {
      if (this.isClosedMenu) {
        this.toggleSelectedCategory();
      }
    },
  },
  methods: {
    calculateCustomProps(lastChildPosition) {
      this.submenuWidth = this.hasComplexSubCategories
        ? this.$refs.submenu.getBoundingClientRect().width
        : 0;
      this.lastChildPosition = lastChildPosition;
      this.updateSubmenuComplexPosition();
    },
    handleCloseSubmenu() {
      this.toggleSelectedCategory();
      this.$emit('menu:close-submenu');
    },
    toggleSelectedCategory(categoryId = null) {
      this.categoryOpen = this.categoryOpen !== categoryId ? categoryId : null;
    },
    updateSubmenuHeight() {
      const submenuHeight = this.$refs.list.getBoundingClientRect().height || 0;
      this.$store.commit(types.menu.setMenuCategoriesHeight, submenuHeight);
      this.toggleSelectedCategory();
    },
    updateSubmenuComplexPosition() {
      this.submenuComplexPosition = -1;
      if (!('getComputedStyle' in window)) return;
      const { paddingRight = 0 } = window.getComputedStyle(this.$refs.submenu);
      const { rowGap = 0, width = 0 } = window.getComputedStyle(
        this.$refs.list
      );
      const separationList = parseFloat(rowGap) || 0;
      const listWidth =
        (parseFloat(width) || 0) + (parseFloat(paddingRight) || 0);
      const maxWidth = this.submenuWidth || window.innerWidth;
      const isComplexList =
        this.hasComplexSubCategories &&
        this.isBreakpointDesktop &&
        this.complexPosition &&
        listWidth + this.complexPosition > maxWidth;
      if (!isComplexList) return;
      const availableWidth = maxWidth - this.complexPosition;
      const columnWidthList = Array.from(this.$refs.list.children).map(
        (item) => item.getBoundingClientRect().width
      );
      if (columnWidthList.length < 2) return;
      const calculateWidthByList = (items) =>
        items.reduce(
          (acc, value) => acc + value,
          (items.length - 1) * separationList
        );
      const columnToUse = columnWidthList
        .keys()
        .toArray()
        .slice(1)
        .find(
          (column) =>
            calculateWidthByList(columnWidthList.slice(column)) <=
            availableWidth
        );
      const submenuComplexPosition = columnToUse
        ? this.complexPosition -
          (calculateWidthByList(columnWidthList.slice(0, columnToUse)) +
            separationList)
        : 0;
      this.submenuComplexPosition =
        submenuComplexPosition >= 0 ? submenuComplexPosition : 0;
    },
  },
};
</script>
